import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  PoliticaPrivacidad,
  PoliticaPrivacidadAceptacion,
} from '@core/models/Registration';
import { RegistrationService } from '@core/http/registration.service';

@Component({
  selector: 'app-privacy-policies-consent',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './privacy-policies-consent.component.html',
  styleUrl: './privacy-policies-consent.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PrivacyPoliciesConsentComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PrivacyPoliciesConsentComponent),
      multi: true,
    },
  ],
})
export class PrivacyPoliciesConsentComponent
  implements OnInit, ControlValueAccessor, Validator {
  @Input() profileType: 'MEDICO' | 'PACIENTE' = 'PACIENTE';
  @Input() title = 'POLITICAS DE PRIVACIDAD';
  @Input() subtitle = 'DEBES LEER Y ACEPTAR LAS POLITICAS VIGENTES PARA CONTINUAR.';
  @Input() pendingTypes: string[] | null = null;
  @Input() autoOpen = false;
  @Input() modalOnly = false;

  primaryPolicy: PoliticaPrivacidad | null = null;
  linkedPolicies: PoliticaPrivacidad[] = [];
  loading = false;
  showModal = false;
  viewerPolicy: PoliticaPrivacidad | null = null;
  viewerReachedEnd = false;
  confirmationPhaseEnabled = false;
  showConfirmationError = false;
  primaryIntro = '';
  confirmationItems: ConfirmationItem[] = [];

  private acceptedMap = new Map<number, boolean>();
  private confirmationAccepted = new Map<ConfirmationItem['key'], boolean>();
  private onChange: (value: PoliticaPrivacidadAceptacion[]) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};
  private consentResolver: ((accepted: boolean) => void) | null = null;

  constructor(private registrationService: RegistrationService) {}

  ngOnInit(): void {
    this.loading = true;
    this.registrationService.getPrivacyPolicies().subscribe({
      next: (response) => {
        const rawPolicies = [...(response.data ?? [])]
          .filter((item) => this.isTruthy(item.activo))
          .filter((item) => this.isAllowedPolicyType(item.tipo));

        this.setPrimaryContentState(
          rawPolicies.find((item) => item.tipo === this.getPrimaryType())?.contenido ?? ''
        );

        const raw = rawPolicies
          .filter((item) => this.isTruthy(item.activo))
          .map((item) => ({
            ...item,
            contenido: this.cleanHtml(item.contenido ?? '', item.tipo),
          }));

        this.primaryPolicy = raw.find((item) => item.tipo === this.getPrimaryType()) ?? null;
        this.linkedPolicies = raw.filter((item) =>
          ['PRIVACIDAD', 'TERMINOS_CONDICIONES'].includes(String(item.tipo).toUpperCase())
        ).sort((a, b) => this.getPolicyOrder(a.tipo) - this.getPolicyOrder(b.tipo));

        for (const policy of this.linkedPolicies) {
          if (!this.acceptedMap.has(policy.idPoliticaPrivacidad)) {
            this.acceptedMap.set(policy.idPoliticaPrivacidad, false);
          }
        }

        this.emitValue();
        this.onValidatorChange();
        this.loading = false;

        if (this.autoOpen && this.hasRequiredConsent) {
          setTimeout(() => this.openConsentModal());
        }
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  writeValue(value: PoliticaPrivacidadAceptacion[] | null): void {
    this.acceptedMap.clear();
    this.confirmationAccepted.clear();
    this.confirmationPhaseEnabled = false;

    for (const item of value ?? []) {
      if (this.primaryPolicy && item.idPoliticaPrivacidad === this.primaryPolicy.idPoliticaPrivacidad) {
        if (!!item.aceptado) {
          this.confirmationPhaseEnabled = true;
          this.markPrimaryConfirmationItems(true);
        }
        continue;
      }

      this.acceptedMap.set(item.idPoliticaPrivacidad, !!item.aceptado);

      const confirmationKey = this.getConfirmationKeyByPolicyId(item.idPoliticaPrivacidad);
      if (confirmationKey && !!item.aceptado) {
        this.confirmationAccepted.set(confirmationKey, true);
      }
    }
  }

  registerOnChange(fn: (value: PoliticaPrivacidadAceptacion[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  validate(_: AbstractControl): ValidationErrors | null {
    return this.allRequiredAccepted ? null : { requiredPolicies: true };
  }

  isAccepted(idPoliticaPrivacidad: number): boolean {
    return !!this.acceptedMap.get(idPoliticaPrivacidad);
  }

  async ensureAccepted(): Promise<boolean> {
    if (this.loading) {
      return false;
    }

    if (!this.hasRequiredConsent || this.allRequiredAccepted) {
      return true;
    }

    if (this.documentsReadyForConfirmation && this.confirmationPhaseEnabled) {
      this.showConfirmationError = true;
      this.onTouched();
      this.onValidatorChange();
      return false;
    }

    this.openConsentModal();

    return new Promise<boolean>((resolve) => {
      this.consentResolver = resolve;
    });
  }

  openConsentModal(): void {
    this.showModal = true;
    this.viewerPolicy = null;
    this.viewerReachedEnd = false;
    this.showConfirmationError = false;
  }

  closeConsentModal(accepted: boolean): void {
    this.showModal = false;
    this.viewerPolicy = null;
    this.viewerReachedEnd = false;
    this.onTouched();

    if (this.consentResolver) {
      this.consentResolver(accepted);
      this.consentResolver = null;
    }
  }

  openDocument(policy?: PoliticaPrivacidad): void {
    if (!policy) {
      return;
    }

    this.showModal = true;
    this.viewerPolicy = policy;
    this.viewerReachedEnd = false;
  }

  backToSummary(): void {
    this.viewerPolicy = null;
    this.viewerReachedEnd = false;
  }

  onDocumentScroll(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 12) {
      this.viewerReachedEnd = true;
    }
  }

  acceptViewedPolicy(): void {
    if (!this.viewerPolicy || !this.viewerReachedEnd) {
      return;
    }

    this.acceptedMap.set(this.viewerPolicy.idPoliticaPrivacidad, true);
    this.emitValue();
    this.onTouched();
    this.onValidatorChange();
    this.backToSummary();
  }

  finishConsentFlow(): void {
    if (!this.documentsReadyForConfirmation) {
      return;
    }

    this.confirmationPhaseEnabled = true;
    this.emitValue();
    this.onValidatorChange();
    if (this.modalOnly) {
      return;
    }

    this.closeConsentModal(false);
  }

  completeConsentFlow(): void {
    if (!this.allRequiredAccepted) {
      this.showConfirmationError = true;
      this.onTouched();
      this.onValidatorChange();
      return;
    }

    this.closeConsentModal(true);
  }

  private emitValue(): void {
    const value = this.linkedPolicies.map((policy) => ({
      idPoliticaPrivacidad: policy.idPoliticaPrivacidad,
      aceptado: !!this.acceptedMap.get(policy.idPoliticaPrivacidad) && this.isPolicyConfirmationChecked(policy),
    }));

    if (this.primaryPolicy) {
      value.unshift({
        idPoliticaPrivacidad: this.primaryPolicy.idPoliticaPrivacidad,
        aceptado: this.isPrimaryPolicyConfirmed,
      });
    }

    this.onChange(value);
  }

  private cleanHtml(html: string, tipo?: string): string {
    if (!html) return '';

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    doc.querySelectorAll('script, form, button, footer').forEach((node) => node.remove());
    doc.querySelectorAll('input, select, textarea').forEach((node) => node.remove());
    doc.querySelectorAll('.confirmaciones').forEach((node) => node.remove());

    doc.querySelectorAll('*').forEach((element) => {
      Array.from(element.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (name.startsWith('on') || name === 'data-action') {
          element.removeAttribute(attr.name);
        }
      });
    });

    if (String(tipo).toUpperCase().startsWith('POPUP_')) {
      doc.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((node, index) => {
        if (index < 2) {
          node.remove();
        }
      });

      Array.from(doc.querySelectorAll('p')).forEach((p) => {
        const text = (p.textContent ?? '').toLowerCase();
        if (
          text.includes('para acceder a su cuenta') ||
          text.includes('informaci') && text.includes('adicional') ||
          text.includes('configurable desde el perfil del m')
        ) {
          p.remove();
        }
      });
    }

    return doc.body.innerHTML;
  }

  private setPrimaryContentState(primaryContent: string): void {
    const parser = new DOMParser();
    const doc = parser.parseFromString(primaryContent ?? '', 'text/html');
    const intro = doc.querySelector('p');

    this.primaryIntro = intro?.textContent?.trim() ?? '';
    this.confirmationItems = Array.from(doc.querySelectorAll('.confirmaciones label'))
      .map((label, index) => this.mapConfirmationItem(label.textContent ?? '', index))
      .filter((item): item is ConfirmationItem => !!item);

    for (const item of this.confirmationItems) {
      if (!this.confirmationAccepted.has(item.key)) {
        this.confirmationAccepted.set(item.key, false);
      }
    }
  }

  private mapConfirmationItem(text: string, index: number): ConfirmationItem | null {
    const normalizedText = text.replace(/\s+/g, ' ').trim();
    if (!normalizedText) {
      return null;
    }

    if (index === 0) {
      return {
        key: 'TERMINOS_CONDICIONES',
        text: normalizedText,
      };
    }

    if (index === 1) {
      return {
        key: 'PRIVACIDAD',
        text: normalizedText,
      };
    }

    return {
      key: `PRIMARY_${index}`,
      text: normalizedText,
    };
  }

  private isTruthy(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private getPrimaryType(): string {
    return this.profileType === 'MEDICO' ? 'POPUP_MEDICO' : 'POPUP_PACIENTE';
  }

  private getPolicyOrder(tipo: string): number {
    const normalized = String(tipo).toUpperCase();
    if (normalized === 'PRIVACIDAD') return 1;
    if (normalized === 'TERMINOS_CONDICIONES') return 3;
    return 99;
  }

  private isAllowedPolicyType(tipo: string): boolean {
    const normalized = String(tipo).toUpperCase();
    if (normalized === 'COOKIES') {
      return false;
    }

    if (!this.pendingTypes?.length) {
      return true;
    }

    return this.pendingTypes
      .map((item) => String(item).toUpperCase())
      .includes(normalized);
  }

  get requiredPolicies(): PoliticaPrivacidad[] {
    return this.linkedPolicies.filter((item) => this.isTruthy(item.requiereAceptacion));
  }

  get allRequiredAccepted(): boolean {
    return this.documentsReadyForConfirmation
      && this.confirmationPhaseEnabled
      && this.allConfirmationItemsConfirmed;
  }

  get hasRequiredConsent(): boolean {
    return this.requiredPolicies.length > 0 || this.requiresPrimaryAcceptance;
  }

  get privacyPolicy(): PoliticaPrivacidad | undefined {
    return this.linkedPolicies.find((item) => String(item.tipo).toUpperCase() === 'PRIVACIDAD');
  }

  get termsPolicy(): PoliticaPrivacidad | undefined {
    return this.linkedPolicies.find((item) => String(item.tipo).toUpperCase() === 'TERMINOS_CONDICIONES');
  }

  get requiresPrimaryAcceptance(): boolean {
    return !!this.primaryPolicy && (this.confirmationItems.length > 2 || this.isTruthy(this.primaryPolicy.requiereAceptacion));
  }

  isConfirmationAccepted(item: ConfirmationItem): boolean {
    return !!this.confirmationAccepted.get(item.key);
  }

  onConfirmationToggle(item: ConfirmationItem, event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (!target) {
      return;
    }

    this.confirmationAccepted.set(item.key, !!target.checked);
    this.showConfirmationError = false;
    this.emitValue();
    this.onTouched();
    this.onValidatorChange();
  }

  get documentsReadyForConfirmation(): boolean {
    return this.requiredPolicies.every((item) => !!this.acceptedMap.get(item.idPoliticaPrivacidad));
  }

  get allConfirmationItemsConfirmed(): boolean {
    return this.confirmationItems.every((item) => this.isConfirmationAccepted(item));
  }

  get isPrimaryPolicyConfirmed(): boolean {
    if (!this.confirmationItems.length) {
      return this.confirmationPhaseEnabled && this.documentsReadyForConfirmation;
    }

    return this.allConfirmationItemsConfirmed;
  }

  private isPolicyConfirmationChecked(policy: PoliticaPrivacidad): boolean {
    const confirmationKey = this.getConfirmationKeyByPolicyId(policy.idPoliticaPrivacidad);
    if (!confirmationKey) {
      return true;
    }

    return !!this.confirmationAccepted.get(confirmationKey);
  }

  private markPrimaryConfirmationItems(value: boolean): void {
    for (const item of this.confirmationItems) {
      if (item.key.startsWith('PRIMARY_')) {
        this.confirmationAccepted.set(item.key, value);
      }
    }
  }

  private getConfirmationKeyByPolicyId(policyId: number): ConfirmationItem['key'] | null {
    if (this.termsPolicy && this.termsPolicy.idPoliticaPrivacidad === policyId) {
      return 'TERMINOS_CONDICIONES';
    }

    if (this.privacyPolicy && this.privacyPolicy.idPoliticaPrivacidad === policyId) {
      return 'PRIVACIDAD';
    }

    return null;
  }

  isReadingConfirmation(item: ConfirmationItem): boolean {
    if (item.key === 'TERMINOS_CONDICIONES') {
      return !!this.termsPolicy;
    }

    if (item.key === 'PRIVACIDAD') {
      return !!this.privacyPolicy;
    }

    return false;
  }

  openConfirmationDocument(item: ConfirmationItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (item.key === 'TERMINOS_CONDICIONES') {
      this.openDocument(this.termsPolicy);
      return;
    }

    if (item.key === 'PRIVACIDAD') {
      this.openDocument(this.privacyPolicy);
    }
  }
}

interface ConfirmationItem {
  key: 'TERMINOS_CONDICIONES' | 'PRIVACIDAD' | `PRIMARY_${number}`;
  text: string;
}
